import React, { ComponentType, ReactNode, Suspense, forwardRef } from 'react';

/**
 * Retry a dynamic import a few times before giving up — smooths over transient
 * network blips / a chunk requested mid-deploy. A genuine 404 (stale chunk after
 * a redeploy) still rejects after the retries. Backoff is short and linear.
 */
function retryImport<T>(load: () => Promise<T>, retries = 3, delay = 400): () => Promise<T> {
    return () =>
        new Promise<T>((resolve, reject) => {
            const attempt = (n: number) => {
                load().then(resolve).catch((err) => {
                    if (n <= 0) return reject(err);
                    setTimeout(() => attempt(n - 1), delay);
                });
            };
            attempt(retries);
        });
}

/**
 * Wraps a dynamically imported component so it can be handed to registerWidget /
 * registerUI (or sit on the WidgetDesignerComponents global) without pulling its
 * module — and its dependency tree — into the initial widget-designer.min.js.
 *
 * - The returned component exists synchronously, so eval-time registration keeps
 *   working while the real component code splits into its own chunk.
 * - It wraps its own <Suspense>, so the host's ComponentRenderer needs no extra
 *   Suspense boundary; the widget's preLoader covers the fallback.
 * - Refs are forwarded (React.lazy passes them to forwardRef components).
 *
 * Copied from uxp/src/lazy.tsx — kept local because widget-designer's generated
 * uxp/components .d.ts does not (yet) export these helpers.
 */
export function lazyComponent<P = any>(
    load: () => Promise<{ default: ComponentType<P> }>,
    fallback: ReactNode = null
) {
    const Lazy = React.lazy(retryImport(load));
    const Wrapped = forwardRef<any, P>((props, ref) => (
        <Suspense fallback={fallback}>
            <Lazy {...(props as any)} ref={ref} />
        </Suspense>
    ));
    Wrapped.displayName = 'WDLazy';
    return Wrapped;
}

/** Same idea for a named export of a module. */
export function lazyNamed<P = any>(
    load: () => Promise<any>,
    exportName: string,
    fallback: ReactNode = null
) {
    return lazyComponent<P>(
        () => load().then((m) => ({ default: m[exportName] })),
        fallback
    );
}
